import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Copy, Check } from "lucide-react";
import { Eyebrow } from "@/components/Reveal";
import { SITE } from "@/constants/site";

export default function Contact() {
    const [copied, setCopied] = useState(false);

    const copyEmail = async () => {
        try {
            await navigator.clipboard.writeText(SITE.email);
            setCopied(true);
            toast.success("Email copied to clipboard");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Could not copy — email us at " + SITE.email);
        }
    };

    const cards = [
        {
            id: "phone",
            icon: Phone,
            label: "Call us",
            value: SITE.phoneDisplay,
            href: SITE.phoneHref,
            hint: "Fastest way to reach us",
            testId: "contact-card-phone",
        },
        {
            id: "email",
            icon: Mail,
            label: "Email us",
            value: SITE.email,
            href: `mailto:${SITE.email}`,
            hint: "We reply within one business day",
            testId: "contact-card-email",
        },
        {
            id: "location",
            icon: MapPin,
            label: "Where we are",
            value: SITE.location,
            href: null,
            hint: "Remote-friendly, everywhere",
            testId: "contact-card-location",
        },
        {
            id: "hours",
            icon: Clock,
            label: "Office hours",
            value: SITE.hours,
            href: null,
            hint: "After-hours? Leave a voicemail",
            testId: "contact-card-hours",
        },
    ];

    return (
        <div data-testid="page-contact">
            <section className="border-b border-ink/10 bg-bone">
                <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
                    <Eyebrow>Contact</Eyebrow>
                    <h1 className="mt-6 font-serif text-5xl font-medium leading-[1.05] tracking-tight sm:text-6xl">
                        Say <span className="italic text-gold-deep">hello.</span>
                    </h1>
                    <p className="mt-6 max-w-xl text-base leading-relaxed text-clay sm:text-lg">
                        No forms to fill in, no portals to navigate. Call or
                        email and you'll reach a real CPA.
                    </p>
                </div>
            </section>

            <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {cards.map((c) => {
                        const Icon = c.icon;
                        const inner = (
                            <>
                                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-bone text-gold-deep">
                                    <Icon className="h-4.5 w-4.5" />
                                </span>
                                <div className="mt-6">
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">
                                        {c.label}
                                    </p>
                                    <p className="mt-2 break-words font-serif text-xl font-medium leading-snug">
                                        {c.value}
                                    </p>
                                    <p className="mt-2 text-xs text-clay">
                                        {c.hint}
                                    </p>
                                </div>
                            </>
                        );
                        return c.href ? (
                            <a
                                key={c.id}
                                href={c.href}
                                data-testid={c.testId}
                                className="flex h-full min-h-[13rem] flex-col rounded-2xl border border-ink/10 bg-cream p-8 transition-colors hover:border-gold"
                            >
                                {inner}
                            </a>
                        ) : (
                            <div
                                key={c.id}
                                data-testid={c.testId}
                                className="flex h-full min-h-[13rem] flex-col rounded-2xl border border-ink/10 bg-cream p-8"
                            >
                                {inner}
                            </div>
                        );
                    })}
                </div>
            </section>

            <section className="mx-auto max-w-6xl px-4 pb-20 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <Eyebrow>What happens next</Eyebrow>
                        <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight sm:text-4xl">
                            Three easy steps.
                        </h2>
                        <div className="mt-8 space-y-7">
                            {[
                                ["You reach out", "Call or email — whatever feels easiest."],
                                ["We listen", "A free 20-minute call about your business and where your books stand."],
                                ["You get a plan & a price", "A clear proposal with a flat fee, in writing, before any work begins."],
                            ].map(([title, desc], i) => (
                                <div key={title} className="flex gap-6" data-testid="contact-step">
                                    <span className="font-serif text-3xl italic text-gold-deep">
                                        {i + 1}.
                                    </span>
                                    <div>
                                        <h3 className="font-serif text-xl font-medium">
                                            {title}
                                        </h3>
                                        <p className="mt-1 max-w-md text-sm leading-relaxed text-clay">
                                            {desc}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <div
                            className="flex h-full flex-col justify-between rounded-2xl bg-ink p-8 text-cream sm:p-10"
                            data-testid="contact-quick-panel"
                        >
                            <div>
                                <h3 className="font-serif text-2xl font-medium leading-snug sm:text-3xl">
                                    The fastest intro is a two-minute phone
                                    call.
                                </h3>
                                <p className="mt-4 text-sm leading-relaxed text-cream/70">
                                    No prep needed — just tell us a little
                                    about your business and we'll take it from
                                    there.
                                </p>
                            </div>
                            <div className="mt-8 flex flex-col gap-3">
                                <a
                                    href={SITE.phoneHref}
                                    data-testid="contact-call-btn"
                                    className="flex items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-ink transition-colors hover:bg-gold-deep hover:text-cream"
                                >
                                    <Phone className="h-4 w-4" />
                                    {SITE.phoneDisplay}
                                </a>
                                <button
                                    onClick={copyEmail}
                                    data-testid="contact-copy-email-btn"
                                    className="flex items-center justify-center gap-3 rounded-full border border-cream/25 px-8 py-4 text-sm font-semibold text-cream transition-colors hover:border-gold hover:text-gold"
                                >
                                    {copied ? (
                                        <Check className="h-4 w-4" />
                                    ) : (
                                        <Mail className="h-4 w-4" />
                                    )}
                                    {SITE.email}
                                    {!copied && (
                                        <Copy className="h-4 w-4 opacity-60" />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
